import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import Heading from "core/Heading";
import SectionContainer from "core/SectionContainer";
import ButtonGroup from "core/ButtonGroup";

import pricingHeroBackdrop from "assets/img/pricing-hero-backdrop.svg";

import { BEFORE_SHOW_DESKTOP_TABLE_MIN_SCREEN_WIDTH } from "./data";

import {
    Card,
    CardTitle,
    CardDescription,
    CardCta,
    CardNonMobileContentContainer
} from "core/Card";
import { MarketingPageSectionHeadingContainer } from "core/MarketingPageSectionHeading";
import { easingSeconds } from "core/animation";
import { P } from "core/Typography";
import Button from "core/Button";
import breakpoints from "core/breakpoints";
import colors from "core/colors";
import { spacingWithBreakpoints } from "core/spacing";
import {
    FeatureTableMobileColumnHeading,
    FeatureTableMobileList
} from "core/FeatureTableMobile";
import { Table } from "core/Table";

import CC from "core/ReusableIcons/CC";

import { convertPxToRem } from "frontend/components/helpers";
import Flexbox from "core/Flexbox";

const browserIsIE = window.document.documentMode;

export const CreativeCommonsIcon = styled(CC).attrs({
    size: { width: 20, height: 22 }
})``;

export const PageAnchor = styled.div`
    padding-top: 2rem;
    margin-top: -2rem;

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        padding-top: 3.125rem;
        margin-top: -3.125rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        padding-top: 4rem;
        margin-top: -4rem;
    }
`;

export const StyledPricingMarketingPage = styled.div`
    color: ${colors.secondary.orca};
`;

export const StyledLink = styled.a`
    color: ${colors.secondary.oolong};
    transition: all ${easingSeconds};

    &:hover {
        color: ${colors.secondary.cosmic};
    }
`;

export const Footnote = styled(P).attrs({ size: 14 })`
    margin: 0 auto;
    text-align: center;
`;

/* -------------------------------------------------------------------------- */
/*                                    HERO                                    */
/* -------------------------------------------------------------------------- */

export const DesktopLineBreak = styled.br`
    @media (max-width: ${breakpoints.beforeDesktop.rem}) {
        display: none;
    }
`;

export const HeroSectionOuterContainer = styled.div`
    height: auto;

    /* for tablet and desktop, height is dependent on height of 
    cards. The bottom of this element matches with 
    bottom of the cards, so change height accordingly. */

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        height: 41.5625rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        padding-bottom: 1.25rem;
        height: 54.375rem;
    }
`;

export const HeroSectionInnerContainer = styled(SectionContainer)`
    text-align: center;
    background-image: url(${pricingHeroBackdrop});
    background-repeat: no-repeat;
    background-position: center top;

    padding-top: 4rem; // 64px - 5xl
    height: auto;

    /* Mobile: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        background-size: 100%;
    }

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        padding-top: 6.125rem; // 98px - 5xl
        height: 36.75rem;
        background-color: ${colors.primary.diamond};
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        padding-top: 8rem; // 128px - 5xl
        height: 42.1875rem;
    }
`;

export const HeroContentContainer = styled.div`
    margin-left: auto;
    margin-right: auto;

    /* Tablet and desktop: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
    }

    /* Tablet: */
    @media (max-width: ${breakpoints.beforeDesktop.rem}) {
        width: 45.4375rem;
    }

    /* Mobile: */
    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        width: 15rem;
    }
`;

/* cards */
export const StyledHeroCard = styled(Card)`
    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        width: 14.3125rem;
        height: 24.125rem;
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        width: 21.125rem;
        height: 31.5rem;
    }
`;

export const HeroCardNonMobileContentContainer = styled(
    CardNonMobileContentContainer
)(css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
`);

export const HeroCardDescription = styled(CardDescription)`
    @media (min-width: ${breakpoints.tablet.rem}) {
        width: 98%;
        flex-grow: 1;
    }

    @media (min-width: ${breakpoints.desktop.rem}) {
        width: 100%;
        flex-grow: 1;
    }
`;

const internetExplorerHeroCardGridContainerStyles = css`
    .MuiGrid-root.MuiGrid-container {
        width: 16rem;

        @media (min-width: ${breakpoints.tablet.rem}) {
            width: 46.6875rem;
        }

        @media (min-width: ${breakpoints.desktop.rem}) {
            width: 67.125rem;
        }
    }
`;

export const HeroCardsContainer = styled.div`
    display: inline-block;
    margin: 0 auto;
    ${spacingWithBreakpoints.XL4Top}

    ${browserIsIE && internetExplorerHeroCardGridContainerStyles}
`;

/* -------------------------------------------------------------------------- */
/*                           ICON PLANS AND PRICING                           */
/* -------------------------------------------------------------------------- */

export const IconsSectionSectionContainer = styled(SectionContainer)`
    ${Footnote} {
        margin-top: 1.5rem; /* 24px custom */

        @media (min-width: ${breakpoints.tablet.rem}) {
            margin-top: 2rem; /* 32px custom */
        }
    }
    background-color: ${({ bgColor }) => bgColor};
`;

export const EduDiscount = styled.span`
    display: block;
    margin: 0.5rem auto 2rem;
    color: ${colors.accent.lightMode.persian};

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        margin-bottom: 1.5rem;
    }
`;

/* ---------------------------------- TABLE --------------------------------- */

export const IconsPricingTableContainer = styled.div`
    text-align: center;
`;

export const IconsPricingTableButtonGroup = styled(ButtonGroup)`
    margin-bottom: 2rem;

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        margin-bottom: 1.5rem;
    }
`;

export const IconsPricingFeatureTableMobileColumnHeading = styled(
    FeatureTableMobileColumnHeading
)`
    height: 17.1875rem;
`;

export const IconsPricingTableMobileHighlight = styled(Table)`
    border: 6px solid ${colors.accent.lightMode.persian};

    ${FeatureTableMobileList} {
        height: 17.8125rem;
    }

    ${IconsPricingFeatureTableMobileColumnHeading} {
        height: 16.875rem;
    }
`;

const bestValueBottomMargin = css`
    margin-bottom: 1.0625rem;
`;

export const IconsPricingTableHeaderBestValuePlaceholder = styled.div`
    /* make sure this height is equal
    to the height of the 
    IconsPricingTableHeaderBestValue element  */
    min-height: 1.4431rem; /* 23.09px */
    ${bestValueBottomMargin}
`;

const IconsPricingTableHeaderBestValue = styled.p`
    display: inline-block;
    font-size: 0.75rem;
    color: ${colors.accent.lightMode.persian};
    padding: 0.16rem 0.6rem;
    border: 1px solid ${colors.accent.lightMode.persian};
    ${bestValueBottomMargin}
`;

IconsPricingTableHeaderBestValue.propTypes = {
    hide: PropTypes.bool
};

export { IconsPricingTableHeaderBestValue };

export const IconsPricingTableHeaderTitle = styled(Heading)`
    margin-bottom: 0.875rem;

    ${CreativeCommonsIcon} {
        position: relative;
        top: 0.25rem; /* 4px */
    }
`;

export const IconsPricingTableHeaderPrice = styled(Heading).attrs({
    type: "p",
    size: "lg",
    alignment: "center"
})`
    line-height: ${convertPxToRem({ px: 44 })};
`;

export const IconsPricingTableHeaderPriceModifier = styled(P).attrs({
    bold: true
})`
    font-size: 0.75rem;
    line-height: 1rem;
`;

const bestValueCtaColors = css`
    color: ${colors.primary.diamond};
    border: 2px solid ${colors.accent.lightMode.persian};

    &,
    &:focus {
        background-color: ${colors.accent.lightMode.persian};
    }

    &:hover {
        background-color: ${colors.accent.lightMode.pansy};
        border: 2px solid ${colors.accent.lightMode.pansy};
    }
`;

export const EduPriceContainer = styled(Flexbox).attrs({
    flexDirection: "row",
    justifyContent: "center"
})``;

export const EduStrikeThroughPrice = styled(Heading).attrs({
    size: "sm",
    type: "p"
})`
    text-decoration: line-through;
    margin-right: ${convertPxToRem({ px: 8 })};
    line-height: ${convertPxToRem({ px: 44 })};
`;

export const EduPrice = styled(Heading).attrs({
    size: "sm",
    type: "p",
    headingColor: colors.accent.lightMode.persian
})`
    line-height: ${convertPxToRem({ px: 44 })};
`;

export const IconsPricingTableHeaderCta = styled(Button).attrs({
    size: "md",
    width: convertPxToRem({ px: 170 })
})`
    &&& {
        max-width: 80%;
        margin-top: ${convertPxToRem({ px: 16 })};
        ${({ bestValueHighlight }) =>
            bestValueHighlight &&
            css`
                ${bestValueCtaColors};
                /* these styles are necessary so that
                  absolutely positioned on top */
                position: relative;
                z-index: 10;
            `}
    }
`;

export const IconsPricingTableHeader = styled.div`
    text-align: center;
    padding: 2rem 0;

    ${CreativeCommonsIcon} {
        margin: 0 0.125rem;
        ${browserIsIE && "height: 0.9375rem;"}
    }
`;

export const IconsPricingTableDesktopContainer = styled.div`
    position: relative;
    margin: 0 auto;
    max-width: 75rem;
`;

/* -------------------------------------------------------------------------- */
/*                               PHOTOS PRICING                               */
/* -------------------------------------------------------------------------- */

/* ---------------------------------- TABLE --------------------------------- */

export const PhotosPricingTableHeaderTitle = styled(Heading)``;

export const PhotosPricingTableHeaderDescription = styled.p`
    font-size: 0.9rem; /* 14.4px custom */
    line-height: 1.6rem;
    margin: 0 auto;
    max-width: 85%;

    /* Tablet and desktop: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        max-width: 94%;
    }
`;

export const PhotosPricingTableHeaderPrice = styled.p`
    line-height: 3rem;
    font-size: 1.5rem;

    /* Tablet: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        font-size: 1.625rem; /* 26px custom */
    }

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        font-size: 2.1975rem; /* 35.16px custom */
    }
`;

export const PhotosPricingTableHeader = styled.div`
    text-align: center;
    padding: 1.5rem 0 1.5rem 0;

    /* Desktop: */
    @media (min-width: ${breakpoints.desktop.rem}) {
        padding: 1.25rem 0 22px 0;
    }

    & > *:not(:last-child) {
        ${spacingWithBreakpoints.MDBottom}
    }

    ${CreativeCommonsIcon} {
        margin: 0 0.375rem; /* 0px 6px */
    }
`;

/* ---------------------------- BULLET LIST CARD ---------------------------- */

export const StyledPhotosSectionCard = styled(Card)`
    border: 1px solid ${colors.primary.scheifer};
    height: auto;

    /* Tablet and desktop: */
    @media (min-width: ${breakpoints.tablet.rem}) {
        /* fixed height is so that the total
        height of these cards when they are stacked
        on tablet view is equal to the height of the 
        neighboring card */
        height: 11.9063rem; /* 190.5px */
    }

    /* Tablet and mobile: */
    @media (max-width: ${BEFORE_SHOW_DESKTOP_TABLE_MIN_SCREEN_WIDTH}) {
        *:last-child > & {
            border-top: none;
        }
    }
`;

/* --------------------------------- LAYOUT --------------------------------- */

export const PhotosSectionHeadingContainer = styled(
    MarketingPageSectionHeadingContainer
)`
    ${spacingWithBreakpoints.XL3Bottom}
`;

export const TableAndCardContainer = styled.div`
    margin: 0 auto;
    width: 100%;
    ${spacingWithBreakpoints.LGBottom}

    @media (min-width: ${breakpoints.desktop.rem}) {
        max-width: 58.75rem;
    }

    & > *:not(:last-child) {
        margin-bottom: 2rem;
    }
`;

/* -------------------------------------------------------------------------- */
/*                         ICON API PLANS AND PRICING                         */
/* -------------------------------------------------------------------------- */

/* ------------------------------ OUR CUSTOMERS ----------------------------- */

export const LogoSet = styled.div`
    display: inline-flex;
    margin: 1.5rem auto 0 auto;
`;

export const OurCustomersContainer = styled.div`
    text-align: center;
    margin: 1.5rem auto 3rem auto;

    @media (min-width: ${breakpoints.tablet.rem}) {
        margin-bottom: 3.5rem;
    }

    @media (min-width: ${breakpoints.tablet.rem}) {
        margin-bottom: 4rem;
    }

    ${LogoSet} {
        /* logos (which are either svg or png) */
        & > * {
            height: 2.5rem;
            width: auto;

            &:not(:last-child) {
                margin-right: 1.25rem;

                @media (min-width: ${breakpoints.tablet.rem}) {
                    margin-right: 3rem;
                }
            }
        }
    }
`;
